import { useState } from 'react';
import { FileText, CheckCircle, BookOpen, GraduationCap } from 'lucide-react';
import StatCard from '@/shared/components/StatCard';

type ProgressTab = 'enabled' | 'course';

interface SubmissionSummaryStatsProps {
    total: number;
    successfulDistinctExercises: number;
    progressEnabledPercent: number;
    progressCoursePercent: number;
    solvedEnabledLabel?: string;
    solvedCourseLabel?: string;
}

export default function SubmissionSummaryStats({
    total,
    successfulDistinctExercises,
    progressEnabledPercent,
    progressCoursePercent,
    solvedEnabledLabel,
    solvedCourseLabel,
}: SubmissionSummaryStatsProps) {
    const [tab, setTab] = useState<ProgressTab>('enabled');
    const toggleTab = () => setTab((t) => (t === 'enabled' ? 'course' : 'enabled'));
    const pct = tab === 'enabled' ? progressEnabledPercent : progressCoursePercent;
    const frac = tab === 'enabled' ? solvedEnabledLabel : solvedCourseLabel;
    const isEn = tab === 'enabled';

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 md:items-stretch gap-6 mb-8">
            <StatCard
                icon={FileText}
                iconColorClass="text-primary-600"
                label="Total Envíos"
                value={total}
                className="h-full flex flex-col justify-center"
            />
            <StatCard
                icon={CheckCircle}
                iconColorClass="text-success-600"
                label="Exitosos (ejercicios distintos)"
                value={successfulDistinctExercises}
                className="h-full flex flex-col justify-center"
            />
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex h-full flex-col justify-center gap-2">
                <h3
                    className="text-sm font-semibold text-gray-900 leading-snug flex flex-wrap items-center gap-x-2 gap-y-1"
                    id="progress-card-heading"
                >
                    <span className="text-gray-600 font-medium">Progreso:</span>
                    <button
                        type="button"
                        id="progress-mode-button"
                        aria-controls="progress-panel"
                        title={
                            isEn
                                ? 'Clic para ver progreso sobre la cursada completa'
                                : 'Clic para ver progreso sobre ejercicios habilitados'
                        }
                        className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-2 px-2.5 py-1.5 text-sm font-semibold shadow-sm transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                            isEn
                                ? 'border-primary-300 bg-primary-50 text-primary-800 hover:border-primary-400 hover:bg-primary-100 focus-visible:ring-primary-500'
                                : 'border-success-300 bg-success-50 text-success-900 hover:border-success-400 hover:bg-success-100 focus-visible:ring-success-500'
                        }`}
                        onClick={toggleTab}
                    >
                        {isEn ? (
                            <BookOpen className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
                        ) : (
                            <GraduationCap className="h-4 w-4 shrink-0 text-success-700" aria-hidden />
                        )}
                        <span>{isEn ? 'Habilitados' : 'Cursada'}</span>
                    </button>
                </h3>
                <div
                    id="progress-panel"
                    role="region"
                    aria-labelledby="progress-mode-button"
                    className="flex items-baseline justify-between gap-2 min-h-[1.75rem]"
                >
                    <span
                        className={`text-2xl font-bold tabular-nums leading-none ${
                            isEn ? 'text-primary-700' : 'text-success-800'
                        }`}
                    >
                        {pct}%
                    </span>
                    {frac && (
                        <span className="text-[11px] text-gray-500 tabular-nums shrink-0">{frac}</span>
                    )}
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                            isEn ? 'bg-primary-600' : 'bg-success-600'
                        }`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>
        </div>
    );
}
